import { useEffect } from "react";

const SITE_NAME = "Quartier am Hornschen Tor";
const DEFAULT_DESCRIPTION = "Offizielle Informationsplattform zum Quartier am Hornschen Tor in Detmold. Aktuelle Baufortschritte, News, Termine, FAQs und Verkehrsinformationen.";
const DEFAULT_OG_IMAGE = "https://baustelleninfo.com/og-image.jpg";
const SITE_URL = "https://baustelleninfo.com";

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [name, key] = selector.replace(/[[\]"']/g, "").split("=");
    el.setAttribute(name.startsWith("meta") ? "name" : name, key);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setOrCreate(tagName, selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => { if (v != null) el.setAttribute(k, v); });
  return el;
}

/**
 * Tiny SEO hook — updates document.title, meta description, canonical, og:* and twitter:* tags.
 * @param {{ title?: string, description?: string, path?: string, image?: string, type?: string }} opts
 */
export default function useSEO({ title, description, path, image, type = "website" } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Bauvorhaben in Detmold`;
    const desc = (description || DEFAULT_DESCRIPTION).slice(0, 300);
    const url = `${SITE_URL}${path || (typeof window !== "undefined" ? window.location.pathname : "/")}`;
    const img = image || DEFAULT_OG_IMAGE;

    document.title = fullTitle;
    setOrCreate("meta", 'meta[name="description"]', { name: "description", content: desc });
    setOrCreate("link", 'link[rel="canonical"]', { rel: "canonical", href: url });
    setOrCreate("meta", 'meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setOrCreate("meta", 'meta[property="og:description"]', { property: "og:description", content: desc });
    setOrCreate("meta", 'meta[property="og:url"]', { property: "og:url", content: url });
    setOrCreate("meta", 'meta[property="og:type"]', { property: "og:type", content: type });
    setOrCreate("meta", 'meta[property="og:image"]', { property: "og:image", content: img });
    setOrCreate("meta", 'meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setOrCreate("meta", 'meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    setOrCreate("meta", 'meta[name="twitter:image"]', { name: "twitter:image", content: img });
  }, [title, description, path, image, type]);
}

/** Strip HTML and collapse whitespace for descriptions */
export function stripHtml(html, max = 200) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text;
}

/** Inject a JSON-LD <script> for structured data. Replaces existing by id. */
export function injectJsonLd(id, data) {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}
